import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { load } from "@cashfreepayments/cashfree-js";
import { FileDown, Loader2 } from "lucide-react";
import api from "@/lib/api";

function money(n) {
  const v = Number(n || 0);
  return Math.round((Number.isFinite(v) ? v : 0) * 100) / 100;
}
function inr(n) {
  return `₹${money(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatInvoiceWhen(v) {
  if (!v) return "";
  const s = String(v).trim();
  const hasZone = /(?:[zZ]|[+-]\d{2}:\d{2})$/.test(s);
  const d = new Date(hasZone || !s.includes("T") ? s : `${s}Z`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

export default function PublicInvoice() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState("");
  const [payErr, setPayErr] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  const loadInv = () => {
    api.get(`/public/invoices/${token}`)
      .then(({ data }) => setInv(data))
      .catch(() => setError("not-found"));
  };
  useEffect(() => { loadInv(); }, [token]);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("paid")) {
      loadInv();
      const t = setTimeout(loadInv, 1200);
      const t2 = setTimeout(loadInv, 4000);
      return () => { clearTimeout(t); clearTimeout(t2); };
    }
  }, []);

  const startPay = async (kind) => {
    setPaying(kind);
    setPayErr("");
    try {
      const { data } = await api.post(`/public/invoices/${token}/checkout`, { kind });
      if (data.payment_link_url) {
        window.location.href = data.payment_link_url;
        return;
      }
      if (!data.payment_session_id) {
        setPayErr("Payment is not ready yet. Ask your salesperson to publish the invoice again.");
        setPaying("");
        return;
      }
      const cashfree = await load({ mode: data.sdk_mode || inv?.sdk_mode || "sandbox" });
      await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
    } catch (e) {
      setPayErr(e?.response?.data?.detail || "Could not open checkout. Try again.");
      setPaying("");
    }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const { data } = await api.get(`/public/invoices/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv?.number || "invoice"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setPayErr("Could not download PDF. Try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  if (error) {
    return <div className="min-h-screen bg-[#EEF2F6] text-slate-600 flex items-center justify-center p-8">This invoice is not available.</div>;
  }
  if (!inv) {
    return <div className="min-h-screen bg-[#EEF2F6] text-slate-500 flex items-center justify-center">Loading invoice…</div>;
  }

  const due = money(inv.amount_due);
  const phases = inv.phases || [];
  const nextPhase = inv.next_phase || phases.find((p) => money(p.amount_due) > 0);
  const hasPhases = phases.length > 0 && due > 0;
  const phaseDue = nextPhase ? money(nextPhase.amount_due || nextPhase.total) : 0;

  return (
    <div className="min-h-screen bg-[#EEF2F6] text-slate-900 py-4 px-3 sm:py-8 sm:px-4 overflow-x-hidden" data-testid="public-invoice">
      <Helmet><title>{inv.number} · Invoice</title></Helmet>
      <div className="max-w-2xl mx-auto w-full min-w-0">
        <div className="rounded-2xl sm:rounded-[28px] overflow-hidden shadow-xl border border-slate-200 bg-white">
          <div className="px-4 sm:px-8 pt-5 sm:pt-8 pb-5 sm:pb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between bg-gradient-to-br from-white to-slate-50">
            <img src={inv.logo_url} alt="MeraStartup" className="h-8 w-auto max-w-[min(70%,180px)] object-contain" data-testid="inv-logo" />
            <div className="sm:text-right min-w-0">
              <div className="text-[11px] uppercase tracking-[0.22em] text-teal-700/80">Invoice</div>
              <div className="font-display text-xl sm:text-2xl font-bold text-slate-900 break-all">{inv.number}</div>
              <div className="text-xs text-slate-500 mt-1 capitalize">{String(inv.status || "").replace("_", " ")}</div>
              <button
                type="button"
                onClick={downloadPdf}
                disabled={pdfBusy}
                data-testid="inv-download-pdf"
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 border border-teal-200 rounded-full px-3 py-1.5 hover:bg-teal-50 disabled:opacity-60"
              >
                {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                Download PDF
              </button>
            </div>
          </div>

          <div className="px-4 sm:px-8 py-5 sm:py-6 grid sm:grid-cols-2 gap-5 sm:gap-6 border-t border-slate-100">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-slate-400">Billed to</div>
              <div className="mt-1 font-semibold text-base sm:text-lg break-words">{inv.customer?.name}</div>
              {inv.customer?.company ? <div className="text-sm text-slate-500 break-words">{inv.customer.company}</div> : null}
              {inv.customer?.email ? <div className="text-sm text-slate-500 break-all">{inv.customer.email}</div> : null}
              {inv.customer?.phone ? <div className="text-sm text-slate-500">+91 {inv.customer.phone}</div> : null}
              {inv.customer?.address ? <div className="text-sm text-slate-400 mt-1 break-words">{inv.customer.address}</div> : null}
            </div>
            <div className="sm:text-right min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-slate-400">From</div>
              <div className="mt-1 font-semibold break-words">{inv.company || "Grocode"}</div>
              <div className="text-sm text-slate-500 break-all">GSTIN {inv.gstin}</div>
              <div className="text-sm text-slate-500 mt-1 break-words" data-testid="inv-from-address">
                {inv.seller_address || "Raghuleela Mall, Kandivali West, Mumbai 400067"}
              </div>
              <div className="text-sm text-slate-500" data-testid="inv-from-support">
                Support{" "}
                <a className="text-teal-700 hover:underline break-all" href={`mailto:${inv.support_email || "support@merastartup.in"}`}>
                  {inv.support_email || "support@merastartup.in"}
                </a>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-8 pb-4">
            <div className="rounded-2xl bg-slate-50 border border-slate-200 overflow-hidden">
              <div className="grid grid-cols-[minmax(0,1fr)_4.75rem_4.75rem] sm:grid-cols-[minmax(0,1fr)_6.5rem_6.5rem] gap-x-2 text-[10px] sm:text-[11px] uppercase tracking-wider text-slate-400 px-3 sm:px-4 py-2 border-b border-slate-200">
                <div>Description</div><div className="text-right">Amount</div><div className="text-right">GST</div>
              </div>
              <div className="px-3 sm:px-4 py-3 grid grid-cols-[minmax(0,1fr)_4.75rem_4.75rem] sm:grid-cols-[minmax(0,1fr)_6.5rem_6.5rem] gap-x-2 text-xs sm:text-sm">
                <div className="min-w-0 break-words">Services</div>
                <div className="text-right font-mono-nums tabular-nums">{inr(inv.base_amount)}</div>
                <div className="text-right font-mono-nums tabular-nums">{inv.gst_enabled ? inr(inv.gst_amount) : "—"}</div>
              </div>
              {(inv.deliverables || []).map((d) => (
                <div key={d.id || d.title} className="px-3 sm:px-4 py-2 border-t border-slate-200 text-xs sm:text-sm" data-testid="inv-deliverable">
                  <div className="text-slate-900 break-words">{d.title}</div>
                  {d.detail ? <div className="text-xs text-slate-500 mt-0.5 break-words">{d.detail}</div> : null}
                </div>
              ))}
              {phases.map((p) => (
                <div key={p.id} className="px-3 sm:px-4 py-2 grid grid-cols-[minmax(0,1fr)_4.75rem_4.75rem] sm:grid-cols-[minmax(0,1fr)_6.5rem_6.5rem] gap-x-2 text-[11px] sm:text-xs text-slate-500 border-t border-slate-200">
                  <div className="min-w-0 break-words">
                    {p.title}{p.due_date ? ` · ${p.due_date}` : ""}
                    {p.status === "paid" ? <span className="ml-2 text-emerald-700 font-semibold">Paid</span> : null}
                  </div>
                  <div className="text-right font-mono-nums tabular-nums">{inr(p.base_amount)}</div>
                  <div className="text-right font-mono-nums tabular-nums">{inr(p.total)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="px-4 sm:px-8 pb-6 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-mono-nums">{inr(inv.base_amount)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">GST {inv.gst_enabled ? "18%" : "off"}</span><span className="font-mono-nums">{inr(inv.gst_amount)}</span></div>
            <div className="flex justify-between text-lg font-bold"><span>Total</span><span className="font-mono-nums text-teal-700">{inr(inv.total)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">Paid</span><span className="font-mono-nums">{inr(inv.amount_paid)}</span></div>
            <div className="flex justify-between text-base font-semibold" data-testid="inv-amount-due"><span>Amount due</span><span className="font-mono-nums text-amber-700">{inr(due)}</span></div>
          </div>

          {due > 0 ? (
            <div className="px-4 sm:px-8 pb-8 space-y-3">
              {hasPhases && nextPhase ? (
                <>
                  <button
                    type="button"
                    onClick={() => startPay("phase")}
                    disabled={!!paying}
                    data-testid="inv-pay-phase"
                    className="block w-full text-center rounded-2xl bg-teal-600 hover:bg-teal-500 disabled:opacity-60 text-white font-bold py-3.5 shadow-lg shadow-teal-600/20"
                  >
                    {paying === "phase" ? "Opening checkout…" : `Pay ${nextPhase.title} ${inr(phaseDue)}`}
                  </button>
                  <button
                    type="button"
                    onClick={() => startPay("full")}
                    disabled={!!paying}
                    data-testid="inv-pay-now"
                    className="block w-full text-center rounded-2xl border-2 border-teal-600 text-teal-800 hover:bg-teal-50 disabled:opacity-60 font-bold py-3.5"
                  >
                    {paying === "full" ? "Opening checkout…" : `Pay Full Payment ${inr(due)}`}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => startPay("full")}
                  disabled={!!paying}
                  data-testid="inv-pay-now"
                  className="block w-full text-center rounded-2xl bg-teal-600 hover:bg-teal-500 disabled:opacity-60 text-white font-bold py-3.5 shadow-lg shadow-teal-600/20"
                >
                  {paying === "full" ? "Opening checkout…" : `Pay ${inr(due)} Now`}
                </button>
              )}
              <p className="text-center text-xs text-slate-400">Payments are non-refundable once work is delivered</p>
              {payErr ? <div className="text-sm text-red-600 mt-2 text-center">{payErr}</div> : null}
            </div>
          ) : (
            <div className="px-4 sm:px-8 pb-8 text-center text-emerald-700 font-semibold">Paid in full</div>
          )}

          {(inv.payments || []).length > 0 && (
            <div className="px-4 sm:px-8 pb-8">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Payment history</div>
              <div className="space-y-1.5">
                {inv.payments.map((p) => (
                  <div key={p.id} className="flex justify-between gap-3 text-xs text-slate-500 font-mono-nums">
                    <span className="min-w-0 break-words">{p.status} · {formatInvoiceWhen(p.created_at)}</span>
                    <span className="shrink-0">{inr(p.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {inv.notes ? <div className="px-4 sm:px-8 pb-6 text-sm text-slate-500 break-words">{inv.notes}</div> : null}

          <div className="px-4 sm:px-8 py-4 border-t border-slate-100 text-center text-[11px] tracking-[0.18em] uppercase text-slate-400" data-testid="inv-powered">
            {inv.powered_by || "Powered By Grocode"}
          </div>
        </div>
      </div>
    </div>
  );
}
